import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { Archive, RotateCcw, Copy, Trash2, AlertTriangle } from 'lucide-react';
import { resolveFirebaseImageUrl } from '../../../Helpers/ImageLoader';
import { getCoverPhotoStoragePath } from '../../../Helpers/utils';
import { placeholderForBuildingType } from '../../../Helpers/PlaceholderUtils';
import { componentLocationLabel, componentLocationSubtitle } from '../../../Helpers/componentLocationLabel';
import {
    WorkOrderWithBuilding,
    WorkOrderStatus,
    WorkOrderPriority,
    CompletionData,
    ConditionInput,
    getComponentKeys,
    getAssignees,
    getDelegations
} from '../../../types/workOrders';
import { BuildingSummary, BaseComponent, Building, BuildingModel } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { workOrdersApi, SourceIssueAction, WorkOrderPhotoType } from '../../../api/workOrdersApi';
import { issuesApi } from '../../../api/issuesApi';
import { projectsApi } from '../../../api/projectsApi';
import { ProjectModel, ProjectStatus } from '../../../types/projects';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref as dbRef, get } from 'firebase/database';
import { useUsersData, Userprofile, toExternalUsers } from '../../../Helpers/UsersContext';
import { useDelegates } from '../../../hooks/useDelegates';
import WorkOrderRemindersDetail from '../../../Components/WorkOrderReminders/WorkOrderRemindersDetail';
import { copyableLinkUrl } from '../../../utils/linkValidity';
import { useBuildComponentTypes } from '../../../contexts/BuildComponentTypesContext';
import AssignWorkOrderModal from '../../../Components/AssignWorkOrderModal';
import MiniMap from '../../../Components/MiniMap';
import PhotoViewerModal from '../../../Components/PhotoViewerModal';
import { useComponentLocationImages } from '../../../hooks/workOrders/useComponentLocationImages';
import ChangeProjectModal from './ChangeProjectModal';
import ComponentPickerModal from './ComponentPickerModal';
import { useWorkOrderSections } from '../../../hooks/workOrders/useWorkOrderSections';
import { useCompletionDraft } from '../../../hooks/workOrders/useCompletionDraft';
import { WorkOrderSectionsEditor } from '../../WorkOrders/components/WorkOrderSectionsEditor';
import { WORK_ORDER_LABELS as L } from '../../WorkOrders/workOrderSections';
import {
    WorkOrderHeader,
    WorkOrderBadges,
    BuildingCard,
    DetailsList,
    WorkOrderTop,
    usePhotoViewer,
    isWorkOrderOverdue,
    ComponentList,
    IssuePhotosSection,
    PhotoGroup,
    AttachmentsList,
    DocumentationSection,
    NotesSection,
    ChecklistsSection,
    CompletionForm,
    CompletionSummary,
    AutosaveIndicator,
    validateCompletion,
    AttachmentRow,
    ComponentListItem,
    ExternalAssigneeRow,
    ReportedIssuesSection
} from '../../WorkOrders/components/shared';
import { useResolvedPhotoUrls } from './useResolvedPhotoUrls';
import { useReportedIssues } from './useReportedIssues';
import ReportIssueModal from '../../../Components/ReportIssueModal';
import { applyComponentPatch } from '../../../Helpers/componentPatch';
import './WorkOrderDetailModal.css';

interface WorkOrderDetailModalProps {
    show: boolean;
    onHide: () => void;
    workOrderKey: string;
    onCompleted?: () => void;
    onCancelled?: () => void;
    /** Called after an admin permanently deletes the work order, so the parent can refresh its lists (a source issue may have been restored to the active issues). */
    onDeleted?: () => void;
    /** Called after an archived work order is re-issued back to active, so the parent can refresh its (archive) list. */
    onReissued?: () => void;
    /** Called when the admin wants to duplicate this work order. The parent opens the create modal pre-filled from this source. */
    onDuplicate?: (workOrder: WorkOrderWithBuilding) => void;
    /** Called after an in-modal edit that changes a field shown in parent list views (title, assignee, priority, due date, components, project link) so the parent can refresh its list. */
    onChanged?: () => void;
    getUserName: (uid: string) => string;
    /** Called after a new external user is created, so the parent can refresh its user list */
    onExternalUserCreated?: () => void;
}

const DESCRIPTION_MAX = 2000;
const AFTER_PHOTOS_ID = 'wo-after-photos';

const componentHref = (buildingKey: string, componentKey: string): string =>
    `/building/${buildingKey}/component/${componentKey}`;

/**
 * Admin work-order detail modal. Renders the canonical sections in
 * WORK_ORDER_SECTIONS order (see workOrderSections.ts) with the admin
 * controls in place on each section.
 */
const WorkOrderDetailModal: React.FC<WorkOrderDetailModalProps> = ({
    show,
    onHide,
    workOrderKey,
    onCompleted,
    onCancelled,
    onDeleted,
    onReissued,
    onChanged,
    onDuplicate,
    getUserName,
    onExternalUserCreated
}) => {
    const { users } = useUsersData();
    const { getTranslation, getLifetime } = useBuildComponentTypes();

    const [workOrder, setWorkOrder] = useState<WorkOrderWithBuilding | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [componentNames, setComponentNames] = useState<Record<string, string>>({});
    const [componentLifetimes, setComponentLifetimes] = useState<Record<string, number | null>>({});
    const [components, setComponents] = useState<Record<string, BaseComponentImpl>>({});
    const [buildingSummary, setBuildingSummary] = useState<BuildingSummary | null>(null);
    // True once the summary read has confirmed the building no longer exists
    // (deleted after the work order was created). Distinct from "not loaded yet".
    const [buildingMissing, setBuildingMissing] = useState(false);
    const [coverPhotoUrl, setCoverPhotoUrl] = useState<string | undefined>(undefined);
    const [copiedLink, setCopiedLink] = useState(false);

    // Confirmations
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [sourceIssueArchived, setSourceIssueArchived] = useState(false);
    const [sourceIssueAction, setSourceIssueAction] = useState<SourceIssueAction>('keep');
    const [showReissueConfirm, setShowReissueConfirm] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);

    // Inline editors
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionValue, setDescriptionValue] = useState('');
    const [savingDescription, setSavingDescription] = useState(false);
    const [descriptionError, setDescriptionError] = useState<string | null>(null);
    const [editingDueDate, setEditingDueDate] = useState(false);
    const [dueDateValue, setDueDateValue] = useState('');
    const [savingDueDate, setSavingDueDate] = useState(false);
    const [dueDateError, setDueDateError] = useState<string | null>(null);

    // Components
    const [showComponentPicker, setShowComponentPicker] = useState(false);
    const [fullBuilding, setFullBuilding] = useState<Building | null>(null);
    const [showReportIssue, setShowReportIssue] = useState(false);
    const [savingComponents, setSavingComponents] = useState(false);

    // Project
    const [availableProjects, setAvailableProjects] = useState<ProjectModel[]>([]);
    const [selectedProjectKey, setSelectedProjectKey] = useState<string>('');
    const [showProjectDropdown, setShowProjectDropdown] = useState(false);
    const [showChangeProjectModal, setShowChangeProjectModal] = useState(false);

    // Completion
    const [completionError, setCompletionError] = useState<string | null>(null);
    const [defaultInvestmentPercentageLoaded, setDefaultInvestmentPercentageLoaded] = useState(false);

    const currentUser = getAuth().currentUser;
    const currentUid = currentUser?.uid || '';
    const currentUserProfile = currentUser ? users?.find((u: Userprofile) => u.key === currentUser.uid) : undefined;
    const isAdmin = currentUserProfile?.admin || false;

    const isOpen = workOrder?.status === WorkOrderStatus.OPEN;
    const canEdit = isAdmin && isOpen;
    // Fel hittade under arbetet: while the order is open, and read-only for
    // admins on a completed order that has reports (molnfast-react#1652).
    // iOS and the guest page hide it once the order is completed.
    const showCompletedReports = isAdmin && workOrder?.status === WorkOrderStatus.COMPLETED;
    const reportedIssues = useReportedIssues(workOrderKey, !!isOpen || showCompletedReports, fullBuilding, componentNames);

    // The delegates' personal links (admin callable); refetched with the work order.
    const delegateCount = Object.keys(getDelegations(workOrder)).length;
    const delegates = useDelegates('workOrder', workOrder?.key ?? null, show && isAdmin && delegateCount > 0);

    // Permanent delete: open and cancelled work orders, plus any work order
    // whose building is gone. Completed work orders are history and stay.
    const canDelete = isAdmin && !!workOrder && (
        buildingMissing || workOrder.status !== WorkOrderStatus.COMPLETED
    );
    const offerSourceIssueChoice = sourceIssueArchived && !buildingMissing;

    // Check if current user can complete this work order (multi-assignee): no
    // internal assignees → anyone may complete; otherwise the current user must
    // be one of the assignees (or an admin). Mirrors the server guard in
    // complete.ts and the iOS canComplete gate — single source of truth.
    const canComplete = (() => {
        if (!workOrder || workOrder.status !== WorkOrderStatus.OPEN) return false;
        if (isAdmin) return true;
        const assigneeUids = Object.keys(getAssignees(workOrder));
        return assigneeUids.length === 0 || (!!currentUser && assigneeUids.includes(currentUser.uid));
    })();

    const draft = useCompletionDraft(
        currentUid && workOrderKey ? `workOrderCompletionDraft:${currentUid}:${workOrderKey}` : null
    );

    // Load work order
    const loadWorkOrder = useCallback(async (opts?: { silent?: boolean }) => {
        if (!workOrderKey) return;
        if (!opts?.silent) setLoading(true);
        setError(null);
        try {
            const data = await workOrdersApi.getById(workOrderKey);
            if (!data) {
                setError('Ärendet hittades inte');
                return;
            }
            setWorkOrder(data);
        } catch (err) {
            console.error('Error loading work order:', err);
            setError('Kunde inte ladda ärendet');
        } finally {
            if (!opts?.silent) setLoading(false);
        }
    }, [workOrderKey]);

    const refetchSilent = useCallback(() => loadWorkOrder({ silent: true }), [loadWorkOrder]);
    const refetchAndNotify = useCallback(() => {
        void loadWorkOrder({ silent: true });
        onChanged?.();
    }, [loadWorkOrder, onChanged]);
    const applyLocalUpdate = useCallback(
        (updater: (wo: WorkOrderWithBuilding) => WorkOrderWithBuilding) =>
            setWorkOrder(prev => (prev ? updater(prev) : prev)),
        []
    );
    // Section toggles (optimistic) and the notes/checklists/photos mutations,
    // all of which reconcile via a silent refetch.
    const sections = useWorkOrderSections({
        buildingKey: workOrder?.buildingKey || '',
        workOrderKey: workOrderKey || '',
        onChanged: refetchSilent,
        applyLocalUpdate,
    });

    useEffect(() => {
        if (show) {
            loadWorkOrder();
            setShowCancelConfirm(false);
            setShowDeleteConfirm(false);
            setShowReissueConfirm(false);
            setShowProjectDropdown(false);
            setEditingDescription(false);
            setDescriptionError(null);
            setEditingDueDate(false);
            setDueDateError(null);
            setCompletionError(null);
            setDefaultInvestmentPercentageLoaded(false);
            setFullBuilding(null);
        }
    }, [show, loadWorkOrder]);

    // Look up the source issue so the delete dialog knows whether to offer a restore.
    useEffect(() => {
        setSourceIssueArchived(false);
        const sourceIssueKey = workOrder?.sourceIssueKey;
        if (!sourceIssueKey || !isAdmin) return;
        let cancelled = false;
        issuesApi.getArchivedById(sourceIssueKey)
            .then(issue => {
                if (!cancelled) setSourceIssueArchived(!!issue);
            })
            .catch(err => console.error('Error loading source issue:', err));
        return () => {
            cancelled = true;
        };
    }, [workOrder?.sourceIssueKey, isAdmin]);

    // Fetch available projects when work order is loaded
    useEffect(() => {
        if (!workOrder?.buildingKey) {
            setAvailableProjects([]);
            return;
        }
        const fetchProjects = async () => {
            try {
                const projects = await projectsApi.getProjectsByBuilding(workOrder.buildingKey);
                // Only Planerad and Pågående (not Utförd)
                setAvailableProjects(projects.filter((p: ProjectModel) =>
                    p.status === ProjectStatus.PLANNED || p.status === ProjectStatus.IN_PROGRESS
                ));
                setSelectedProjectKey(workOrder.projectKey || '');
            } catch (err) {
                console.error('Failed to fetch projects:', err);
            }
        };
        fetchProjects();
    }, [workOrder?.buildingKey, workOrder?.projectKey]);

    // Fetch building summary when work order is loaded
    useEffect(() => {
        if (!workOrder?.buildingKey) {
            setBuildingSummary(null);
            setBuildingMissing(false);
            return;
        }
        const fetchBuildingSummary = async () => {
            try {
                const snapshot = await get(dbRef(getDatabase(), `buildingsSummary/${workOrder.buildingKey}`));
                if (snapshot.exists()) {
                    setBuildingSummary(snapshot.val() as BuildingSummary);
                    setBuildingMissing(false);
                } else {
                    setBuildingSummary(null);
                    setBuildingMissing(true);
                }
            } catch (err) {
                console.error('Error fetching building summary:', err);
            }
        };
        fetchBuildingSummary();
    }, [workOrder?.buildingKey]);

    // Cover photo URL (placeholder until the real one resolves)
    useEffect(() => {
        const placeholder = placeholderForBuildingType(buildingSummary?.buildingType);
        setCoverPhotoUrl(placeholder);
        const storagePath = buildingSummary ? getCoverPhotoStoragePath(buildingSummary) : undefined;
        if (!storagePath) return;
        let cancelled = false;
        resolveFirebaseImageUrl(storagePath)
            .then(url => { if (!cancelled) setCoverPhotoUrl(url); })
            .catch(err => console.error('Failed to resolve cover photo:', err));
        return () => { cancelled = true; };
    }, [buildingSummary]);

    // Fetch names and lifetimes for all linked components when the work order
    // is loaded (or its component links change)
    // `buildings/{key}` is the BuildingModel; `Building` wraps it with the summary.
    const toBuilding = (key: string, model: BuildingModel): Building => ({
        key,
        model,
        summary: buildingSummary ?? ({ key } as BuildingSummary),
    });

    const linkedComponentKeys = useMemo(() => (workOrder ? getComponentKeys(workOrder) : []), [workOrder]);

    // "Hitta komponenterna" needs the drawings and map images, so load the full
    // building as soon as there are linked components (the picker reuses it).
    useEffect(() => {
        const buildingKey = workOrder?.buildingKey;
        if (!buildingKey || linkedComponentKeys.length === 0 || fullBuilding?.key === buildingKey) return;
        let cancelled = false;
        get(dbRef(getDatabase(), `buildings/${buildingKey}`))
            .then((snapshot) => {
                if (!cancelled && snapshot.exists()) {
                    setFullBuilding(toBuilding(buildingKey, snapshot.val() as BuildingModel));
                }
            })
            .catch((err) => console.warn('Could not load building for component locations', err));
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toBuilding only reads buildingSummary
    }, [workOrder?.buildingKey, linkedComponentKeys.length, fullBuilding?.key]);

    const locationImages = useComponentLocationImages(fullBuilding, components, linkedComponentKeys);
    const linkedComponentKeysSignature = linkedComponentKeys.join(',');
    useEffect(() => {
        if (!workOrder?.buildingKey || linkedComponentKeys.length === 0) {
            setComponentNames({});
            setComponentLifetimes({});
            setComponents({});
            return;
        }
        const fetchComponentData = async () => {
            const names: Record<string, string> = {};
            const lifetimes: Record<string, number | null> = {};
            const impls: Record<string, BaseComponentImpl> = {};
            await Promise.all(linkedComponentKeys.map(async (componentKey) => {
                try {
                    const snapshot = await get(dbRef(getDatabase(), `buildings/${workOrder.buildingKey}/structure/components/${componentKey}`));
                    if (!snapshot.exists()) return;
                    const componentData = snapshot.val() as BaseComponent;
                    impls[componentKey] = new BaseComponentImpl(componentData);
                    const specificType = componentData?.specificType?.value;
                    // The app-wide component name (label, else roof shape / type /
                    // base type, plus direction), as on the component page
                    names[componentKey] = impls[componentKey].displayName();
                    lifetimes[componentKey] = specificType
                        ? (getLifetime(specificType)?.expectedLifetimeYears ?? null)
                        : null;
                } catch (err) {
                    console.error('Error fetching component:', err);
                    names[componentKey] = 'Komponent';
                    lifetimes[componentKey] = null;
                }
            }));
            setComponentNames(names);
            setComponentLifetimes(lifetimes);
            setComponents(impls);
        };
        fetchComponentData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkedComponentKeysSignature, workOrder?.buildingKey, getTranslation, getLifetime]);

    // Default investment percentage from the maintenance activity, seeded into
    // the draft once (only when the draft has no value yet).
    useEffect(() => {
        if (!canComplete || !workOrder || defaultInvestmentPercentageLoaded) return;
        if (draft.values.actualCostInvestmentPercentage !== null) {
            setDefaultInvestmentPercentageLoaded(true);
            return;
        }
        const loadDefault = async () => {
            let defaultPercentage = 0;
            if (workOrder.maintenanceTemplateId && workOrder.buildingKey) {
                try {
                    const planSnapshot = await get(dbRef(getDatabase(), `maintenance/active/${workOrder.buildingKey}`));
                    if (planSnapshot.exists()) {
                        const plan = planSnapshot.val();
                        for (const componentKey in plan.components) {
                            const activity = plan.components[componentKey]?.maintenanceActivities?.[workOrder.maintenanceTemplateId];
                            if (activity?.investmentPercentage !== undefined) {
                                defaultPercentage = activity.investmentPercentage;
                                break;
                            }
                        }
                    }
                } catch (err) {
                    console.error('Error loading maintenance plan for default investment percentage:', err);
                }
            }
            draft.setValues({ actualCostInvestmentPercentage: defaultPercentage });
            setDefaultInvestmentPercentageLoaded(true);
        };
        loadDefault();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canComplete, workOrder, defaultInvestmentPercentageLoaded]);

    // ----- Handlers -----

    const handleSaveTitle = async (title: string) => {
        if (!workOrderKey) return;
        await workOrdersApi.updateTitle(workOrderKey, title);
        refetchAndNotify();
    };

    const handleChangePriority = async (next: WorkOrderPriority) => {
        if (!workOrderKey) return;
        try {
            await workOrdersApi.updatePriority(workOrderKey, next);
            refetchAndNotify();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte uppdatera prioritet');
        }
    };

    const handleSaveDescription = async () => {
        if (!workOrderKey) return;
        setSavingDescription(true);
        setDescriptionError(null);
        try {
            await workOrdersApi.updateDescription(workOrderKey, descriptionValue);
            setEditingDescription(false);
            loadWorkOrder({ silent: true });
        } catch (err) {
            setDescriptionError(err instanceof Error ? err.message : 'Kunde inte uppdatera beskrivningen');
        } finally {
            setSavingDescription(false);
        }
    };

    // The date input works with the date-only part (YYYY-MM-DD); the API stamps
    // the end-of-day time on save, matching creation.
    const handleSaveDueDate = async () => {
        if (!workOrderKey) return;
        setSavingDueDate(true);
        setDueDateError(null);
        try {
            await workOrdersApi.updateDueDate(workOrderKey, dueDateValue || null);
            setEditingDueDate(false);
            refetchAndNotify();
        } catch (err) {
            setDueDateError(err instanceof Error ? err.message : 'Kunde inte uppdatera förfallodatum');
        } finally {
            setSavingDueDate(false);
        }
    };

    // The component pickers need the full building; loaded on first open.
    const ensureFullBuilding = async (): Promise<boolean> => {
        if (!workOrder) return false;
        if (fullBuilding) return true;
        try {
            const snapshot = await get(dbRef(getDatabase(), `buildings/${workOrder.buildingKey}`));
            if (!snapshot.exists()) {
                setError('Fastigheten kunde inte laddas');
                return false;
            }
            setFullBuilding(toBuilding(workOrder.buildingKey, snapshot.val() as BuildingModel));
            return true;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fastigheten kunde inte laddas');
            return false;
        }
    };

    const openComponentPicker = async () => {
        if (await ensureFullBuilding()) setShowComponentPicker(true);
    };

    // Felanmäl (#1650): any component in the building, or "Annat".
    const openReportIssue = async () => {
        if (await ensureFullBuilding()) setShowReportIssue(true);
    };

    const saveComponentKeys = async (keys: string[]) => {
        if (!workOrderKey) return;
        setSavingComponents(true);
        try {
            await workOrdersApi.updateComponents(workOrderKey, Array.from(new Set(keys)));
            setShowComponentPicker(false);
            // Linked components show in the project's delmoment list — refresh it too.
            refetchAndNotify();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte uppdatera komponent');
        } finally {
            setSavingComponents(false);
        }
    };

    const handleAssignmentChanged = async () => {
        await loadWorkOrder();
        await delegates.refetch();
        onChanged?.();
    };

    const handleCopyLink = async (link: string) => {
        try {
            await navigator.clipboard.writeText(link);
            setCopiedLink(true);
            setTimeout(() => setCopiedLink(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    // Spara in the project dropdown: a real change goes through the confirm
    // modal, where the admin also reviews the due date.
    const handleProjectChange = () => {
        if (!workOrder) return;
        if (selectedProjectKey === (workOrder.projectKey || '')) {
            setShowProjectDropdown(false);
            return;
        }
        setShowChangeProjectModal(true);
    };
    const handleProjectChangeConfirm = async (options: { dueDate?: string | null }) => {
        if (!workOrderKey) return;
        await workOrdersApi.linkToProject(workOrderKey, selectedProjectKey || null, options);
        await loadWorkOrder();
        setShowProjectDropdown(false);
        onChanged?.();
    };
    const getProjectName = (projectKey: string | undefined): string | undefined => {
        if (!projectKey) return undefined;
        return availableProjects.find(p => p.key === projectKey)?.title || 'Okänt projekt';
    };

    const afterPhotoCount = workOrder?.afterPictures ? Object.keys(workOrder.afterPictures).length : 0;
    const completionRules = {
        isOpen: !!isOpen,
        requiresPhoto: !!workOrder?.requiresPhoto,
        requiresComment: !!workOrder?.requiresComment,
        requiresConditionUpdate: !!workOrder?.requiresConditionUpdate,
        componentKeys: linkedComponentKeys,
        afterPhotoCount,
    };

    const handleComplete = async () => {
        if (!workOrderKey || !workOrder) return;
        setCompletionError(null);
        const errors = validateCompletion(draft.values, completionRules);
        if (errors.length > 0) {
            setCompletionError(errors[0]);
            return;
        }
        const completionData: CompletionData = {
            comment: draft.values.comment.trim() || undefined
        };
        if (workOrder.requiresConditionUpdate && linkedComponentKeys.length > 0) {
            const newConditions: Record<string, ConditionInput> = {};
            for (const componentKey of linkedComponentKeys) {
                const input = draft.values.conditions[componentKey];
                if (!input || input.value === null) {
                    setCompletionError(L.validationConditionsRequired);
                    return;
                }
                newConditions[componentKey] = {
                    value: input.value,
                    comment: input.comment?.trim() || undefined
                };
            }
            completionData.newConditions = newConditions;
        }
        if (draft.values.actualCost !== null && draft.values.actualCost >= 0) {
            completionData.actualCost = draft.values.actualCost;
            completionData.actualCostInvestmentPercentage = draft.values.actualCostInvestmentPercentage ?? 0;
        }
        setSubmitting(true);
        try {
            await workOrdersApi.complete(workOrderKey, completionData, { buildingKey: workOrder?.buildingKey, title: workOrder?.title });
            draft.clear();
            onCompleted?.();
            onHide();
        } catch (err) {
            setCompletionError(err instanceof Error ? err.message : 'Kunde inte slutföra ärendet');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (!workOrderKey) return;
        setSubmitting(true);
        try {
            await workOrdersApi.cancel(workOrderKey);
            onCancelled?.();
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte avbryta ärendet');
            setShowCancelConfirm(false);
        } finally {
            setSubmitting(false);
        }
    };

    const openDeleteConfirm = () => {
        setSourceIssueAction(workOrder?.status === WorkOrderStatus.OPEN ? 'restore' : 'keep');
        setShowDeleteConfirm(true);
    };
    const handleDelete = async () => {
        if (!workOrderKey) return;
        setSubmitting(true);
        try {
            await workOrdersApi.delete(workOrderKey, {
                sourceIssue: offerSourceIssueChoice ? sourceIssueAction : 'keep'
            });
            onDeleted?.();
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort ärendet');
            setShowDeleteConfirm(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleReissue = async () => {
        if (!workOrderKey) return;
        setSubmitting(true);
        try {
            await workOrdersApi.reissue(workOrderKey);
            onReissued?.();
            onHide();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte återutfärda ärendet');
            setShowReissueConfirm(false);
        } finally {
            setSubmitting(false);
        }
    };

    // Attachments
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [uploadingPhotoType, setUploadingPhotoType] = useState<WorkOrderPhotoType | null>(null);
    const handleUploadAttachments = async (files: File[]) => {
        if (!workOrderKey) return;
        setUploadingAttachments(true);
        try {
            for (const file of files) {
                const result = await workOrdersApi.uploadAttachment(workOrderKey, file);
                if (!result.success) {
                    setError(result.error || 'Kunde inte ladda upp bilagan');
                    break;
                }
            }
            await refetchSilent();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ladda upp bilagan');
        } finally {
            setUploadingAttachments(false);
        }
    };
    const handleDeleteAttachment = async (attachmentKey: string) => {
        if (!workOrderKey) return;
        try {
            const result = await workOrdersApi.deleteAttachment(workOrderKey, attachmentKey);
            if (result.success) {
                await refetchSilent();
            } else {
                setError(result.error || 'Kunde inte radera bilagan');
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte radera bilagan');
        }
    };
    // Throws so the rename modal can show the error inline
    const handleRenameAttachment = async (attachmentKey: string, displayName: string | undefined) => {
        if (!workOrderKey) return;
        await workOrdersApi.renameAttachment(workOrderKey, attachmentKey, displayName);
        await refetchSilent();
    };
    const handleOpenAttachment = async (attachmentKey: string) => {
        const attachment = workOrder?.attachments?.[attachmentKey];
        if (!attachment) return;
        try {
            const url = await workOrdersApi.getAttachmentDownloadUrl(attachment.storagePath);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte öppna bilagan');
        }
    };

    // Photos: one hidden file input per group
    const photoInputs = useRef<Record<WorkOrderPhotoType, HTMLInputElement | null>>({
        beforePictures: null, progressPictures: null, afterPictures: null
    });
    const handlePhotoFiles = async (photoType: WorkOrderPhotoType, fileList: FileList | null) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        setUploadingPhotoType(photoType);
        try {
            await sections.uploadPhotos(photoType, files);
        } catch {
            // Shown by `sections.error`
        } finally {
            setUploadingPhotoType(null);
        }
    };
    const issuePhotos = useResolvedPhotoUrls(workOrder?.photos);
    const beforePhotos = useResolvedPhotoUrls(workOrder?.beforePictures);
    const progressPhotos = useResolvedPhotoUrls(workOrder?.progressPictures);
    const afterPhotos = useResolvedPhotoUrls(workOrder?.afterPictures);
    // Photos open in the viewer, stepping through their own group
    const photoViewer = usePhotoViewer();
    const openPhoto = (items: { key: string; url: string }[]) => (key: string) => {
        photoViewer.open(items.map(p => p.key), key, (k) => items.find(p => p.key === k)?.url ?? '');
    };

    // ----- Derived view data -----

    const userList = users?.filter((u: Userprofile) => u.accessToWeb && !u.deleted && !u.isExternal) || [];
    const externalUserList = toExternalUsers(users);
    const coordinates = buildingSummary?.coordinates;
    const buildingInfo = buildingSummary?.buildingInfo;
    const hasCoordinates = !!(coordinates?.latitude && coordinates?.longitude);
    const directionsUrl = hasCoordinates
        ? `https://www.google.com/maps/dir/?api=1&destination=${coordinates!.latitude},${coordinates!.longitude}`
        : undefined;
    const addressLines = [
        buildingInfo?.street,
        [buildingInfo?.postalCode, buildingInfo?.city].filter(Boolean).join(' ')
    ].filter((line): line is string => !!line);


    const internalAssignees = workOrder
        ? Object.keys(getAssignees(workOrder)).map(uid => ({ key: uid, name: getUserName(uid) }))
        : [];
    const externalAssignees: ExternalAssigneeRow[] = workOrder
        ? Object.keys(getDelegations(workOrder)).map(uid => ({
            key: uid,
            name: getUserName(uid),
            link: copyableLinkUrl(delegates.byUid[uid]?.link)
        }))
        : [];

    const componentItems: ComponentListItem[] = workOrder
        ? linkedComponentKeys.map(key => {
            const component = components[key];
            const gps = component?.gpsCoordinates?.value;
            const locations = locationImages[key];
            // Level and room (or the surface it sits on) under the name, as the felanmälan card shows it.
            const subtitle = component && fullBuilding
                ? componentLocationSubtitle(componentLocationLabel(component, fullBuilding))
                : null;
            return {
                key,
                name: componentNames[key] || 'Komponent',
                subtitle: subtitle ?? undefined,
                href: componentHref(workOrder.buildingKey, key),
                locations: locations && locations.length > 0 ? locations : undefined,
                gps: gps?.latitude && gps?.longitude ? { lat: gps.latitude, lng: gps.longitude } : undefined
            };
        })
        : [];

    const attachmentRows: AttachmentRow[] = Object.entries(workOrder?.attachments || {}).map(([key, a]) => ({
        key,
        name: a.displayName || a.fileName || key,
        fileName: a.fileName || key,
        displayName: a.displayName,
        sizeBytes: a.fileSize,
        uploadedAt: a.attachmentType === 'file' ? a.uploadedAt : a.date,
        canRemove: isAdmin,
        canRename: isAdmin
    }));

    const enabled = workOrder?.enabledSections;
    const showNotes = isOpen ? enabled?.notes === true : Object.keys(workOrder?.notes || {}).length > 0;
    const showChecklists = isOpen ? enabled?.checklists === true : Object.keys(workOrder?.checklists || {}).length > 0;
    const showBefore = isOpen ? enabled?.beforePictures === true : beforePhotos.length > 0;
    const showProgress = isOpen ? enabled?.progressPictures === true : progressPhotos.length > 0;
    const showAfter = isOpen || afterPhotos.length > 0;
    const canEditPhotos = isOpen || isAdmin;
    const showDocumentation = showNotes || showChecklists || showBefore || showProgress || showAfter;

    const photoGroup = (type: WorkOrderPhotoType, title: string, photos: ReturnType<typeof useResolvedPhotoUrls>, id?: string) => (
        <div id={id} key={type}>
            <PhotoGroup
                title={title}
                photos={photos}
                onAdd={canEditPhotos ? () => photoInputs.current[type]?.click() : undefined}
                uploading={uploadingPhotoType === type}
                onRemove={canEditPhotos ? (photoKey) => { void sections.deletePhoto(type, photoKey); } : undefined}
                onOpen={openPhoto(photos)}
            />
            {canEditPhotos && (
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    ref={(el) => { photoInputs.current[type] = el; }}
                    onChange={(e) => {
                        void handlePhotoFiles(type, e.target.files);
                        e.target.value = '';
                    }}
                />
            )}
        </div>
    );

    return (
    <>
        {/* While the photo viewer is open it owns Escape and focus */}
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            scrollable
            className="wo-detail-modal"
            keyboard={!photoViewer.isOpen}
            enforceFocus={!photoViewer.isOpen}
        >
            <Modal.Header closeButton>
                <Modal.Title as="div" className="me-2">
                    {workOrder ? (
                        <WorkOrderBadges
                            priority={workOrder.priority}
                            status={workOrder.status}
                            dueDate={workOrder.dueDate}
                            onChangePriority={canEdit ? handleChangePriority : undefined}
                        />
                    ) : L.entity}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" style={{ width: '2rem', height: '2rem' }} />
                        <p className="mt-2 text-muted">Laddar ärende...</p>
                    </div>
                ) : error && !workOrder ? (
                    <Alert variant="danger">{error}</Alert>
                ) : workOrder ? (
                    <>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>
                        )}
                        {sections.error && (
                            <Alert variant="danger" dismissible onClose={sections.clearError}>{sections.error}</Alert>
                        )}
                        {/* Orphaned work order: its building has been deleted. Shown
                            first so the reader knows before reading anything else;
                            admins can remove the order permanently from here. */}
                        {buildingMissing && (
                            <Alert variant="warning" className="d-flex align-items-center gap-3 flex-wrap mb-4">
                                <AlertTriangle size={20} className="flex-shrink-0" />
                                <div className="flex-grow-1">
                                    <strong>Fastigheten har tagits bort</strong>
                                    <div className="small">
                                        Ärendet hör till en fastighet som inte längre finns. Komponenter och platsinformation kan inte visas.
                                    </div>
                                </div>
                                {isAdmin && (
                                    <Button
                                        variant="outline-danger"
                                        className="flex-shrink-0"
                                        onClick={openDeleteConfirm}
                                        disabled={submitting}
                                    >
                                        <Trash2 size={14} className="me-1" />
                                        Ta bort ärende
                                    </Button>
                                )}
                            </Alert>
                        )}

                        {canComplete && <AutosaveIndicator state={draft.autosave} className="mb-2" />}

                        {/* 1–3: title, building and description (shared with the guest page) */}
                        <WorkOrderTop
                            header={
                                <WorkOrderHeader
                                    title={workOrder.title}
                                    priority={workOrder.priority}
                                    status={workOrder.status}
                                    dueDate={workOrder.dueDate}
                                    onSaveTitle={canEdit ? handleSaveTitle : undefined}
                                    showBadges={false}
                                />
                            }
                            building={
                                <BuildingCard
                                    name={workOrder.buildingName}
                                    addressLines={addressLines}
                                    coverPhotoUrl={coverPhotoUrl}
                                    directionsUrl={directionsUrl}
                                    buildingHref={buildingMissing ? undefined : `/building/${workOrder.buildingKey}`}
                                    map={hasCoordinates ? (
                                        <MiniMap coordinates={coordinates!} buildingName={workOrder.buildingName} height={160} />
                                    ) : undefined}
                                />
                            }
                            description={workOrder.description}
                            onEditDescription={canEdit ? () => {
                                setDescriptionValue(workOrder.description || '');
                                setDescriptionError(null);
                                setEditingDescription(true);
                            } : undefined}
                            descriptionEditor={editingDescription ? (
                                <>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        maxLength={DESCRIPTION_MAX}
                                        value={descriptionValue}
                                        onChange={(e) => setDescriptionValue(e.target.value)}
                                        disabled={savingDescription}
                                        autoFocus
                                    />
                                    <small className={`d-block mt-1 text-end ${descriptionValue.length >= DESCRIPTION_MAX ? 'text-danger' : 'text-muted'}`}>
                                        {descriptionValue.length} / {DESCRIPTION_MAX}
                                    </small>
                                    {descriptionError && <div className="text-danger small mt-1">{descriptionError}</div>}
                                    <div className="d-flex gap-2 mt-2">
                                        <Button variant="primary" onClick={handleSaveDescription} disabled={savingDescription}>
                                            {savingDescription ? <Spinner animation="border" size="sm" /> : 'Spara'}
                                        </Button>
                                        <Button variant="outline-secondary" onClick={() => setEditingDescription(false)} disabled={savingDescription}>
                                            Avbryt
                                        </Button>
                                    </div>
                                </>
                            ) : undefined}
                            createdAt={workOrder.createdAt}
                            createdByName={getUserName(workOrder.createdBy)}
                        />

                        {/* 4 Detaljer */}
                        <DetailsList
                            createdAt={workOrder.createdAt}
                            showCreated={false}
                            dueDate={workOrder.dueDate}
                            overdue={isWorkOrderOverdue(workOrder.status, workOrder.dueDate)}
                            onEditDueDate={canEdit ? () => {
                                setDueDateValue(workOrder.dueDate ? workOrder.dueDate.split(' ')[0] : '');
                                setDueDateError(null);
                                setEditingDueDate(true);
                            } : undefined}
                            dueDateEditor={editingDueDate ? (
                                <div className="wo-inline-editor">
                                    <Form.Control
                                        type="date"
                                        size="sm"
                                        aria-label={L.dueDate}
                                        value={dueDateValue}
                                        onChange={(e) => setDueDateValue(e.target.value)}
                                        disabled={savingDueDate}
                                    />
                                    <div className="wo-inline-editor__actions">
                                        <Button size="sm" variant="primary" onClick={handleSaveDueDate} disabled={savingDueDate}>
                                            {savingDueDate ? <Spinner animation="border" size="sm" /> : 'Spara'}
                                        </Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setEditingDueDate(false)} disabled={savingDueDate}>
                                            Avbryt
                                        </Button>
                                    </div>
                                    {dueDateError && <div className="text-danger small mt-1">{dueDateError}</div>}
                                </div>
                            ) : undefined}
                            reminders={externalAssignees.length > 0 ? (
                                <WorkOrderRemindersDetail
                                    workOrder={workOrder}
                                    canEdit={canEdit}
                                    nameOf={getUserName}
                                    onSaved={refetchAndNotify}
                                />
                            ) : undefined}
                            projectLabel={getProjectName(workOrder.projectKey)}
                            onEditProject={isAdmin ? () => {
                                setSelectedProjectKey(workOrder.projectKey || '');
                                setShowProjectDropdown(true);
                            } : undefined}
                            projectEditor={showProjectDropdown ? (
                                <div className="wo-inline-editor">
                                    <Form.Select
                                        size="sm"
                                        aria-label={L.project}
                                        value={selectedProjectKey}
                                        onChange={(e) => setSelectedProjectKey(e.target.value)}
                                    >
                                        <option value="">Inget projekt</option>
                                        {availableProjects.map((project: ProjectModel) => (
                                            <option key={project.key} value={project.key}>{project.title}</option>
                                        ))}
                                    </Form.Select>
                                    <div className="wo-inline-editor__actions">
                                        <Button size="sm" variant="primary" onClick={handleProjectChange}>Spara</Button>
                                        <Button size="sm" variant="outline-secondary" onClick={() => setShowProjectDropdown(false)}>Avbryt</Button>
                                    </div>
                                </div>
                            ) : undefined}
                            unitLabel={workOrder.unitLabel}
                            isOpen={!!isOpen}
                            closedStatus={workOrder.status === WorkOrderStatus.CANCELLED ? 'cancelled' : 'completed'}
                            internalAssignees={internalAssignees}
                            externalAssignees={externalAssignees}
                            onEditAssignment={canEdit ? () => setShowAssignModal(true) : undefined}
                            onCopyLink={handleCopyLink}
                        />
                        {copiedLink && <div className="small text-success mb-2">Kopierad</div>}

                        {/* 5 Komponenter */}
                        {!buildingMissing && (
                            <ComponentList
                                components={componentItems}
                                onAdd={canEdit && !savingComponents ? () => { void openComponentPicker(); } : undefined}
                                onRemove={canEdit && !savingComponents
                                    ? (key) => { void saveComponentKeys(linkedComponentKeys.filter(k => k !== key)); }
                                    : undefined}
                                onView={(key) => window.open(componentHref(workOrder.buildingKey, key), '_blank', 'noopener,noreferrer')}
                            />
                        )}

                        {/* 7 Foton från felanmälan */}
                        {issuePhotos.length > 0 && (
                            <IssuePhotosSection photos={issuePhotos} onOpen={openPhoto(issuePhotos)} />
                        )}

                        {/* 8 Bilagor */}
                        <AttachmentsList
                            attachments={attachmentRows}
                            onUpload={isAdmin ? (files) => { void handleUploadAttachments(files); } : undefined}
                            uploading={uploadingAttachments}
                            onRemove={isAdmin ? (key) => { void handleDeleteAttachment(key); } : undefined}
                            onRename={isAdmin ? handleRenameAttachment : undefined}
                            onOpen={(key) => { void handleOpenAttachment(key); }}
                        />

                        {/* 9 Dokumentation */}
                        {showDocumentation && (
                        <DocumentationSection
                            headerExtra={canEdit ? <WorkOrderSectionsEditor workOrder={workOrder} sections={sections} /> : undefined}
                        >
                            {showNotes && (
                                <NotesSection
                                    notes={Object.values(workOrder.notes || {})}
                                    currentUserId={currentUid}
                                    canEdit={!!isOpen}
                                    canDeleteAny={isAdmin}
                                    resolveAuthorName={getUserName}
                                    onAdd={(text) => sections.addNote('', text)}
                                    onUpdate={(noteKey, text) => sections.updateNote(noteKey, '', text)}
                                    onDelete={sections.deleteNote}
                                />
                            )}
                            {showChecklists && (
                                <ChecklistsSection
                                    checklists={Object.values(workOrder.checklists || {})}
                                    currentUserId={currentUid}
                                    canEdit={!!isOpen}
                                    canDeleteAny={isAdmin}
                                    onAddChecklist={sections.addChecklist}
                                    onDeleteChecklist={sections.deleteChecklist}
                                    onAddItem={sections.addChecklistItem}
                                    onToggleItem={sections.toggleChecklistItem}
                                    onDeleteItem={sections.deleteChecklistItem}
                                />
                            )}
                            {showBefore && photoGroup('beforePictures', L.beforePictures, beforePhotos)}
                            {showProgress && photoGroup('progressPictures', L.progressPictures, progressPhotos)}
                            {showAfter && photoGroup('afterPictures', L.afterPictures, afterPhotos, AFTER_PHOTOS_ID)}
                        </DocumentationSection>
                        )}

                        {/* 10 Fel hittade under arbetet — open orders; admins also on a completed one with reports */}
                        {isOpen && (
                            <ReportedIssuesSection
                                onReport={() => { void openReportIssue(); }}
                                rows={reportedIssues.rows}
                            />
                        )}
                        {showCompletedReports && reportedIssues.rows.length > 0 && (
                            <ReportedIssuesSection rows={reportedIssues.rows} />
                        )}

                        {/* 11 Slutför ärende / Slutförande */}
                        {canComplete && (
                            <CompletionForm
                                values={draft.values}
                                onChange={draft.setValues}
                                rules={completionRules}
                                components={linkedComponentKeys.map(key => ({
                                    key,
                                    name: componentNames[key] || 'Komponent',
                                    expectedLifetimeYears: componentLifetimes[key] ?? null
                                }))}
                                showCost
                                onViewComponent={(key) => window.open(componentHref(workOrder.buildingKey, key), '_blank', 'noopener,noreferrer')}
                                onSubmit={handleComplete}
                                submitting={submitting}
                                error={completionError}
                            />
                        )}
                        {workOrder.status === WorkOrderStatus.COMPLETED && (
                            <CompletionSummary
                                completedAt={workOrder.completedAt || ''}
                                completedByName={workOrder.completedBy ? getUserName(workOrder.completedBy) : undefined}
                                comment={workOrder.completionComment}
                                conditions={workOrder.completionConditions}
                                componentNames={componentNames}
                                actualCost={workOrder.actualCost}
                                actualCostInvestmentPercentage={workOrder.actualCostInvestmentPercentage}
                                showCost
                            />
                        )}
                    </>
                ) : null}
            </Modal.Body>
            {workOrder && isAdmin && (
                <Modal.Footer className="wo-modal__footer">
                    {/* Secondary actions grouped left: duplicate (any status),
                        archive (open only) and delete. */}
                    <div className="wo-modal__footer-secondary">
                        {onDuplicate && (
                            <Button variant="outline-secondary" onClick={() => onDuplicate(workOrder)}>
                                <Copy size={16} className="me-1" />
                                {L.duplicate}
                            </Button>
                        )}
                        {isOpen && (
                            <Button variant="outline-secondary" onClick={() => setShowCancelConfirm(true)}>
                                <Archive size={16} className="me-1" />
                                {L.cancelAction}
                            </Button>
                        )}
                        {canDelete && (
                            <Button variant="outline-danger" onClick={openDeleteConfirm}>
                                <Trash2 size={16} className="me-1" />
                                {L.delete}
                            </Button>
                        )}
                    </div>
                    {/* The main action on an archived work order (completed or
                        archived without action): back to active as an open order. */}
                    {(workOrder.status === WorkOrderStatus.COMPLETED || workOrder.status === WorkOrderStatus.CANCELLED) && (
                        <Button variant="primary" onClick={() => setShowReissueConfirm(true)}>
                            <RotateCcw size={16} className="me-1" />
                            {L.reopen}
                        </Button>
                    )}
                </Modal.Footer>
            )}
        </Modal>

        {/* Archive-without-action confirmation — stacked on top of the detail modal. */}
        <Modal
            show={showCancelConfirm}
            onHide={() => !submitting && setShowCancelConfirm(false)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{L.cancelAction}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2"><strong>Är du säker på att du vill arkivera detta ärende utan åtgärd?</strong></p>
                <p className="text-muted mb-0">Ärendet slutförs inte utan arkiveras som återkallat. Det flyttas till arkivet och försvinner från aktiva ärenden.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowCancelConfirm(false)} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="danger" onClick={handleCancel} disabled={submitting}>
                    {submitting ? (
                        <><Spinner size="sm" className="me-1" /> Arkiverar...</>
                    ) : (
                        <><Archive size={16} className="me-1" /> {L.cancelAction}</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Permanent-delete confirmation — stacked on top of the detail modal. */}
        <Modal
            show={showDeleteConfirm}
            onHide={() => !submitting && setShowDeleteConfirm(false)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Ta bort ärende</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2"><strong>Är du säker på att du vill ta bort detta ärende permanent?</strong></p>
                <p className="text-muted mb-0">
                    {buildingMissing && 'Fastigheten som ärendet hör till har tagits bort. '}
                    Ärendet raderas med sina bilder, bilagor och eventuella gästlänkar. Det går inte att ångra.
                </p>
                {workOrder?.status === WorkOrderStatus.OPEN && !buildingMissing && (
                    <p className="text-muted mt-2 mb-0 small">
                        Välj "{L.cancelAction}" i stället om ärendet ska finnas kvar i arkivet och kunna återutfärdas.
                    </p>
                )}
                {workOrder?.status === WorkOrderStatus.OPEN && workOrder.maintenanceTemplateId && !buildingMissing && (
                    <p className="text-muted mt-2 mb-0 small">
                        Ärendet skapades från en underhållsaktivitet. Aktiviteten visas åter under "Att hantera".
                    </p>
                )}
                {offerSourceIssueChoice && (
                    <Form.Group className="mt-3">
                        <Form.Label className="fw-medium">
                            Ärendet skapades från en felanmälan. Vad ska hända med den?
                        </Form.Label>
                        <Form.Check
                            type="radio"
                            id="delete-source-issue-restore"
                            name="delete-source-issue"
                            label="Återställ felanmälan till aktiva felanmälningar"
                            checked={sourceIssueAction === 'restore'}
                            onChange={() => setSourceIssueAction('restore')}
                            disabled={submitting}
                        />
                        <Form.Check
                            type="radio"
                            id="delete-source-issue-keep"
                            name="delete-source-issue"
                            label="Låt felanmälan vara arkiverad"
                            checked={sourceIssueAction === 'keep'}
                            onChange={() => setSourceIssueAction('keep')}
                            disabled={submitting}
                        />
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowDeleteConfirm(false)} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="danger" onClick={handleDelete} disabled={submitting}>
                    {submitting ? (
                        <><Spinner size="sm" className="me-1" /> Tar bort...</>
                    ) : (
                        <><Trash2 size={16} className="me-1" /> Ta bort ärende</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Re-issue confirmation — stacked on top of the detail modal. */}
        <Modal
            show={showReissueConfirm}
            onHide={() => !submitting && setShowReissueConfirm(false)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Återutfärda ärende</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-2"><strong>Är du säker på att du vill återutfärda detta ärende?</strong></p>
                <p className="text-muted mb-2">
                    Ärendet flyttas tillbaka till aktiva ärenden med status {L.statusOpen}.
                    Slutförandeinformation (kommentar, kostnad och vem som slutförde) tas bort,
                    och ärendet kan utföras på nytt.
                </p>
                <p className="text-muted mb-0 small">
                    Obs: skickuppdateringar som redan gjorts på komponenter påverkas inte, och
                    en eventuell extern delegering måste skickas på nytt.
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowReissueConfirm(false)} disabled={submitting}>
                    Avbryt
                </Button>
                <Button variant="primary" onClick={handleReissue} disabled={submitting}>
                    {submitting ? (
                        <><Spinner size="sm" className="me-1" /> Återutfärdar...</>
                    ) : (
                        <><RotateCcw size={16} className="me-1" /> {L.reopen}</>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>

        <PhotoViewerModal {...photoViewer.viewerProps} />

        {workOrder && (
            <AssignWorkOrderModal
                show={showAssignModal}
                onHide={() => setShowAssignModal(false)}
                workOrder={workOrder}
                internalUsers={userList}
                externalUsers={externalUserList}
                delegatesByUid={delegates.byUid}
                onAssigned={handleAssignmentChanged}
                onExternalUserCreated={onExternalUserCreated}
            />
        )}

        {workOrder && (
            <ChangeProjectModal
                show={showChangeProjectModal}
                onHide={() => setShowChangeProjectModal(false)}
                workOrder={workOrder}
                fromProjectTitle={getProjectName(workOrder.projectKey) ?? null}
                toProject={availableProjects.find(p => p.key === selectedProjectKey) ?? null}
                onConfirm={handleProjectChangeConfirm}
            />
        )}

        {workOrder && fullBuilding && (
            <ReportIssueModal
                show={showReportIssue}
                onHide={() => setShowReportIssue(false)}
                buildingKey={workOrder.buildingKey}
                building={fullBuilding}
                sourceWorkOrderKey={workOrder.key}
                onSuccess={() => { void reportedIssues.reload(); }}
            />
        )}

        {fullBuilding && (
            <ComponentPickerModal
                show={showComponentPicker}
                onHide={() => setShowComponentPicker(false)}
                building={fullBuilding}
                multiSelect
                selectedKeys={linkedComponentKeys}
                onSelectMultiple={(keys) => { void saveComponentKeys(keys); }}
                saving={savingComponents}
                onComponentPatched={(componentKey, patch) =>
                    setFullBuilding(prev => applyComponentPatch(prev, componentKey, patch))}
            />
        )}
    </>
    );
};

export default WorkOrderDetailModal;
